import {Drawer, Popover} from '@mantine/core';
import classNames from 'classnames';
import {
  type FC,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {MdUnfoldMore} from 'react-icons/md';
import {useBooleanState} from '../../../../../shared/services/useBooleanState';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {
  SORT_OPTIONS,
  type SortOption,
  type SortOptionValue,
} from '../../../constants/sort';

type Props = {
  handleSort: (sort: SortOptionValue) => void;
  /** ログイン状態をチェックし、未ログイン時はモーダルを表示する */
  requireAuth?: () => boolean;
  className?: string;
};

const PROGRESS_SORT_STORAGE_KEY = 'entry-management-progress-sort-preference';

const getInitialSort = (): SortOptionValue => {
  if (typeof window === 'undefined') return SORT_OPTIONS[0].value;
  const saved = localStorage.getItem(PROGRESS_SORT_STORAGE_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      const foundOption = SORT_OPTIONS.find(
        (o) =>
          o.value.sortBy === parsed.sortBy &&
          o.value.sortOrder === parsed.sortOrder,
      );
      if (foundOption) return foundOption.value;
    } catch {
      // JSON parse error - fall through to default
    }
  }
  return SORT_OPTIONS[0].value;
};

export const ProgressSort: FC<Props> = ({
  handleSort,
  requireAuth,
  className,
}) => {
  const [shown, show, hide] = useBooleanState(false);
  const initialSort = useRef(getInitialSort());
  const [selectedOption, setSelectedOption] = useState<SortOption>(
    SORT_OPTIONS.find(
      (o) =>
        o.value.sortBy === initialSort.current.sortBy &&
        o.value.sortOrder === initialSort.current.sortOrder,
    ) ?? SORT_OPTIONS[0],
  );
  const {isPC} = useMediaQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: 無限レンダリングしてしまうため
  useEffect(() => {
    if (
      initialSort.current.sortBy !== SORT_OPTIONS[0].value.sortBy ||
      initialSort.current.sortOrder !== SORT_OPTIONS[0].value.sortOrder
    ) {
      handleSort(initialSort.current);
    }
  }, []);

  const onClickHandler = useCallback(() => {
    show();
  }, [show]);

  const onCloseHandler = useCallback(() => {
    hide();
  }, [hide]);

  const onOptionClickHandler = useCallback(
    (option: SortOption) => () => {
      hide();
      // 未ログイン時は会員登録導線モーダルを表示
      if (requireAuth && !requireAuth()) {
        return;
      }
      handleSort(option.value);
      setSelectedOption(
        SORT_OPTIONS.find(
          (o) =>
            o.value.sortBy === option.value.sortBy &&
            o.value.sortOrder === option.value.sortOrder,
        ) ?? SORT_OPTIONS[0],
      );
      localStorage.setItem(
        PROGRESS_SORT_STORAGE_KEY,
        JSON.stringify(option.value),
      );
    },
    [handleSort, hide, requireAuth],
  );

  const classes = classNames(
    'py-8 px-12 gap-[6px] flex items-center text-base bg-background text-black rounded-full',
    className,
  );

  return isPC ? (
    <Popover opened={shown} onClose={onCloseHandler} position="bottom-start">
      <Popover.Target>
        <SortButton
          selectedOption={selectedOption}
          onClickHandler={onClickHandler}
          classes={classes}
        />
      </Popover.Target>
      <Popover.Dropdown className="p-0">
        <div className="flex flex-col">
          {SORT_OPTIONS.map((option) => (
            <button
              type="button"
              key={`${option.value.sortBy}-${option.value.sortOrder}`}
              className="flex text-base items-center p-12 hover:bg-background-1"
              onClick={onOptionClickHandler(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  ) : (
    <>
      <SortButton
        selectedOption={selectedOption}
        onClickHandler={onClickHandler}
        classes={classes}
      />
      <Drawer
        opened={shown}
        onClose={onCloseHandler}
        position="bottom"
        size="sm"
        overlayProps={{opacity: 0.48}}
        withCloseButton={false}
        className="p-16"
        styles={{inner: {left: 0}}}
      >
        <div className="text-black-4">ソート</div>
        <div className="mt-16 flex flex-col divide-y divide-line border-y border-line">
          {SORT_OPTIONS.map((option) => (
            <button
              type="button"
              key={`${option.value.sortBy}-${option.value.sortOrder}`}
              className="flex items-center p-8"
              onClick={onOptionClickHandler(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Drawer>
    </>
  );
};

type SortButtonProps = {
  selectedOption: SortOption;
  onClickHandler: () => void;
  classes?: string;
};

const SortButton = forwardRef<HTMLButtonElement, SortButtonProps>(
  ({selectedOption, onClickHandler, classes}, ref) => {
    return (
      <button
        type="button"
        ref={ref}
        className={classes}
        onClick={onClickHandler}
      >
        <span className="text-sm">{selectedOption.label}</span>
        <MdUnfoldMore size={20} />
      </button>
    );
  },
);
